import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { MoreHoriz } from '@mui/icons-material'
import { setCompanyData, setIsSaving, resetState } from '@/redux/ducks/onboarding/main-v2'
import ThemeInterface from '@/theme-interface'
import RegistrationStepCard from './components/registration-step-card'
import { RadioInput } from '@/modules/base/components/inputs'
import { ButtonSquare } from '@/modules/base/components/buttons'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import Facebook from '@assets/images/onboarding/facebookIcon.svg'
import YouTube from '@assets/images/onboarding/youtubeIcon.svg'
import LinkedIn from '@assets/images/onboarding/linkedinIcon.svg'
import Event from '@assets/images/onboarding/eventIcon.svg'
import Podcast from '@assets/images/onboarding/podcastIcon.svg'
import Instagram from '@assets/images/onboarding/instagramIcon.svg'
import OMR from '@assets/images/onboarding/omrIcon.svg'
import Other from '@assets/images/onboarding/otherIcon.svg'
import Recommended from '@assets/images/onboarding/recommendationIcon.svg'
import TikTok from '@assets/images/onboarding/tikTokIcon.svg'
import Google from '@assets/images/onboarding/googleIcon.svg'
import { ICON_LABELS, HUBSPOT_OPTIONS } from '@/modules/base/util/constants'
import RegisterService from '@/modules/_services/register.service'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import { fetchData } from '@/redux/ducks/user/info'
import { user } from 'ampersand-app'
import { HubspotService } from '@/modules/hubspot/hubspotService'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { reset as resetWizard } from '@/redux/ducks/wizard/config'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'
import { isAndroid, isIos } from '@/modules/_helpers/detectMobile'
import openAppAdviceModal from '@/modules/dashboard/helpers/openAppAdviceModal'
import openWelcomeModal from './helpers/open-welcome-modal'

const iconSize = 30
const { crmObjects: { CONTACTS }, propertys: { CONTACT_SOURCES } } = HUBSPOT_OPTIONS

// ⚠️ This is a local test object to simulate the contact sources data.
// const localTestObject = {
//   data: {
//     values: [
//       {
//         value: 'Suchmaschine (Google/Bing)',
//         label: 'Suchmaschine (Google/Bing)',
//       },
//       {
//         value: 'Empfehlung',
//         label: 'Empfehlung',
//       },
//       {
//         value: 'OMR Reviews',
//         label: 'OMR Reviews',
//       },
//       {
//         value: 'LinkedIn',
//         label: 'LinkedIn',
//       },
//       {
//         value: 'Podcast',
//         label: 'Podcast',
//       },
//       {
//         value: 'Facebook',
//         label: 'Facebook',
//       },
//       {
//         value: 'Instagram',
//         label: 'Instagram',
//       },
//       {
//         value: 'TikTok',
//         label: 'TikTok',
//       },
//       {
//         value: 'Youtube',
//         label: 'YouTube',
//       },
//       {
//         value: 'Messe/Event',
//         label: 'Messe/Event',
//       },
//       {
//         value: 'Sonstiges',
//         label: 'Sonstiges',
//       },
//       {
//         value: 'Keine Angabe',
//         label: 'Keine Angabe',
//       },
//     ],
//   },
//   meta: {},
// }

const useStyles = makeStyles((theme: ThemeInterface) => ({
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    width: '100%',
    [theme.breakpoints.down(768)]: {
      gridTemplateColumns: '1fr',
    },
  },
  option: {
    margin: '0 !important',
    minHeight: 52,
    padding: '8px 16px',
    border: `1px solid ${theme.palette.brand.divider}`,
    borderRadius: theme.borderRadiusLarge,
    backgroundColor: theme.backgroundColor,
    cursor: 'pointer',
    transition: 'background-color 0.2s, border-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.brand.main,
    },
    '& input': {
      flexShrink: 0,
      margin: '0 16px 0 0',
      accentColor: theme.palette.brand.main,
      cursor: 'pointer',
    },
  },
  selected: {
    borderColor: theme.palette.brand.main,
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    fontSize: theme.fontSizes.default,
    color: theme.palette.text.primary,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  finishButton: {
    height: 48,
    borderRadius: theme.borderRadiusPill,
    fontSize: theme.fontSizes.smedium,
  },
}))

// registration step 3 of 3 (step 1 is the confirm email modal)
const REGISTRATION_STEP = 3

const iconMap = {
  [ICON_LABELS.SEARCH_ENGINE]: <SVGIcon svg={Google} height={iconSize} width={iconSize} />,
  [ICON_LABELS.RECOMMENDATION]: <SVGIcon svg={Recommended} height={iconSize} width={iconSize} />,
  [ICON_LABELS.OMR_REVIEWS]: <SVGIcon svg={OMR} height={iconSize} width={iconSize} />,
  [ICON_LABELS.PODCAST]: <SVGIcon svg={Podcast} height={iconSize} width={iconSize} />,
  [ICON_LABELS.LINKEDIN]: <SVGIcon svg={LinkedIn} height={iconSize} width={iconSize} />,
  [ICON_LABELS.FACEBOOK]: <SVGIcon svg={Facebook} height={iconSize} width={iconSize} />,
  [ICON_LABELS.INSTAGRAM]: <SVGIcon svg={Instagram} height={iconSize} width={iconSize} />,
  [ICON_LABELS.TIKTOK]: <SVGIcon svg={TikTok} height={iconSize} width={iconSize} />,
  [ICON_LABELS.YOUTUBE]: <SVGIcon svg={YouTube} height={iconSize} width={iconSize} />,
  [ICON_LABELS.EVENT]: <SVGIcon svg={Event} height={iconSize} width={iconSize} />,
  [ICON_LABELS.OTHER]: <SVGIcon svg={Other} height={iconSize} width={iconSize} />,
}

const OnboardingUserExperience = ({ onClose }) => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const [selectedOption, setSelectedOption] = useState(null)
  const [contactSources, setContactSources] = useState([])
  const { passwordData: { password }, ...onboardingData } = useAppSelector(state => state.onboarding.mainV2)
  const { subscriptions } = useAppSelector(state => state.tenant.info)
  const companyData = useAppSelector(state => state.onboarding.mainV2.companyData)
  const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''

  useEffect(() => {
    getContactSources().then((sources) => {
      setContactSources(sources)
      // Do setContactSources(localTestObject.data.values) to test locally.
    })
  }
  , [])
  const getContactSources = async() => {
    return HubspotService.getProperty(CONTACTS, CONTACT_SOURCES)
  }

  const handleSelection = (option) => {
    setSelectedOption(option.value)
    dispatch(setCompanyData({ ...companyData, source: option.value }))
  }

  const handleClose = async() => {
    GtmService.pushEvent({
      event: GtmEventTypes.GaEventHub,
      eventCategory: 'Onboarding',
      eventAction: 'Schließen',
      eventLabel: `Quellabfrage abgebrochen ${subscriptionTypeTypesLabel}`,
    })
    await finishOnboarding()
  }

  const finishOnboarding = async() => {
    dispatch(setCompanyData({ ...companyData }))
    dispatch(setIsSaving(true))
    try {
      if (password) onboardingData.password = password
      const { body: { data: { ...data } } } = await RegisterService.finishOnboarding(onboardingData)

      dispatch(setIsSaving(false))
      dispatch(resetState())
      dispatch(resetWizard())
      user.token = data.token
      user.store()

      dispatch(fetchData())
      toast.showToast({ type: 'success', message: lang.onboardingV2.general.success })
      // the app advice for phones follows the welcome screen instead of opening on top of it
      openWelcomeModal(isAndroid() || isIos() ? openAppAdviceModal : undefined)
      onClose?.()
    } catch (e) {
      console.error(e)
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      dispatch(setIsSaving(false))
    }
  }

  const onButtonClick = async() => {
    GtmService.pushEvent({
      event: GtmEventTypes.GaEventHub,
      eventCategory: 'Onboarding',
      eventAction: 'Weiter',
      eventLabel: `Quellabfrage abgeschlossen ${subscriptionTypeTypesLabel}`,
    })
    await finishOnboarding()
  }

  const options = contactSources?.map((option) => ({
    ...option,
    icon: iconMap[option.value] || <MoreHoriz />,
  })) ?? []

  const radioOptions = options.map((option) => ({
    value: option.value,
    label: (
      <span className={classes.optionLabel}>
        <span className={classes.iconWrapper}>{option.icon}</span>
        {option.label}
      </span>
    ),
  }))

  const onRadioChange = (value: string) => {
    const option = options.find(o => o.value === value)
    if (option) handleSelection(option)
  }

  return (
    <RegistrationStepCard
      step={REGISTRATION_STEP}
      title={lang.registration.userExperience.title}
      text={lang.registration.userExperience.hint}
      onClose={handleClose}
      dataQsId='onboarding-user-experience'
      stickyFooter
      footer={(
        <ButtonSquare
          className={classes.finishButton}
          label={lang.registration.userExperience.finish}
          onClick={onButtonClick}
          disabled={onboardingData.isSaving}
          dataQsId='onboarding-user-experience-finish'
        />
      )}
    >
      <RadioInput
        name='contactSource'
        value={selectedOption ?? ''}
        options={radioOptions}
        onChange={onRadioChange}
        customOptionContainerClass={classes.options}
        customOptionClass={classes.option}
        selectedOptionClass={classes.selected}
        dataQsId='onboarding-user-experience-options'
      />
    </RegistrationStepCard>
  )
}

export default OnboardingUserExperience
